import { createSlice } from "@reduxjs/toolkit";

const MAX_TABS = 10;

// Extract userId from JWT token to store tabs per user
const getUserId = () => {
  try {
    const token = localStorage.getItem("token");
    if (token) {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return payload?.id || "";
    }
  } catch { /* ignore */ }
  return "";
};

const getTabsKey = () => {
  const uid = getUserId();
  return uid ? `bp_tabs_${uid}` : "bp_tabs";
};

// Load persisted tabs from localStorage (per-user key)
const loadTabs = () => {
  try {
    const key = getTabsKey();
    const saved = localStorage.getItem(key);
    if (saved) {
      const parsed = JSON.parse(saved);
      if (parsed.tabs?.length > 0) return parsed;
    }
  } catch (e) { /* ignore */ }
  return { tabs: [], activeTabId: "" };
};

const persisted = loadTabs();

const initialState = {
  tabs: persisted.tabs,
  activeTabId: persisted.activeTabId,
};

const saveTabs = (state) => {
  try {
    const key = getTabsKey();
    localStorage.setItem(key, JSON.stringify({ tabs: state.tabs, activeTabId: state.activeTabId }));
  } catch (e) { /* ignore */ }
};

// Generate a readable title from a route path
const titleFromPath = (path) => {
  const clean = path.replace(/^\//, "").split("/")[0];
  return clean.replace(/([A-Z])/g, " $1").trim() || "Page";
};

const tabSlice = createSlice({
  name: "tabs",
  initialState,
  reducers: {
    openTab(state, action) {
      const { path, title, deduplicate } = action.payload;
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;

      // Only deduplicate when explicitly requested (e.g. auto-open from URL)
      if (deduplicate) {
        const existing = state.tabs.find((t) => t.path === normalizedPath);
        if (existing) {
          state.activeTabId = existing.id;
          saveTabs(state);
          return;
        }
      }

      if (state.tabs.length >= MAX_TABS) {
        const removable = state.tabs.findIndex((t) => t.id !== state.activeTabId);
        if (removable !== -1) state.tabs.splice(removable, 1);
      }
      const newTab = {
        id: `tab-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        path: normalizedPath,
        title: title || titleFromPath(normalizedPath),
      };
      state.tabs.push(newTab);
      state.activeTabId = newTab.id;
      saveTabs(state);
    },

    switchTab(state, action) {
      const tabId = action.payload;
      if (state.tabs.find((t) => t.id === tabId)) {
        state.activeTabId = tabId;
        saveTabs(state);
      }
    },

    closeTab(state, action) {
      const tabId = action.payload;
      const index = state.tabs.findIndex((t) => t.id === tabId);
      if (index === -1) return;

      state.tabs.splice(index, 1);

      // If we closed the active tab, switch to adjacent
      if (state.activeTabId === tabId) {
        if (state.tabs.length > 0) {
          const newIndex = Math.min(index, state.tabs.length - 1);
          state.activeTabId = state.tabs[newIndex].id;
        } else {
          state.activeTabId = "";
        }
      }
      saveTabs(state);
    },

    closeOtherTabs(state, action) {
      const keepId = action.payload;
      state.tabs = state.tabs.filter((t) => t.id === keepId);
      state.activeTabId = keepId;
      saveTabs(state);
    },

    closeAllTabs(state) {
      state.tabs = [];
      state.activeTabId = "";
      saveTabs(state);
    },

    reorderTabs(state, action) {
      const { fromIndex, toIndex } = action.payload;
      const [moved] = state.tabs.splice(fromIndex, 1);
      state.tabs.splice(toIndex, 0, moved);
      saveTabs(state);
    },

    updateTabTitle(state, action) {
      const { tabId, title } = action.payload;
      const tab = state.tabs.find((t) => t.id === tabId);
      if (tab) {
        tab.title = title;
        saveTabs(state);
      }
    },

    // Update the active tab's path when user navigates within a page
    updateActiveTabPath(state, action) {
      const { path, title } = action.payload;
      const normalizedPath = path.startsWith("/") ? path : `/${path}`;
      const tab = state.tabs.find((t) => t.id === state.activeTabId);
      if (tab) {
        tab.path = normalizedPath;
        if (title) tab.title = title;
        saveTabs(state);
      }
    },

    clearTabsOnLogout(state) {
      state.tabs = [];
      state.activeTabId = "";
      try { localStorage.removeItem(getTabsKey()); } catch { /* ignore */ }
    },
  },
});

export const {
  openTab,
  switchTab,
  closeTab,
  closeOtherTabs,
  closeAllTabs,
  reorderTabs,
  updateTabTitle,
  updateActiveTabPath,
  clearTabsOnLogout,
} = tabSlice.actions;

export default tabSlice.reducer;
